import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '../types'
import { api, authApi } from '../services/api'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitializing: boolean
  error: string | null
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string, invitationCode?: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<void>
  updateUser: (data: Partial<User>) => Promise<void>
  fetchUser: () => Promise<void>
  checkAuth: () => Promise<void>
  clearError: () => void
  initialize: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: true,
      error: null,

      initialize: async () => {
        const { isInitializing } = get()
        if (!isInitializing) return
        
        set({ isInitializing: true, isLoading: true })
        
        try {
          const token = localStorage.getItem('halcode-auth')
          if (!token) {
            set({ isInitializing: false, isLoading: false, isAuthenticated: false })
            return
          }
          
          const response = await authApi.me()
          const { user, accessToken, refreshToken } = response.data.data || response.data
          
          if (accessToken) {
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          }
          
          set({ 
            user, 
            isAuthenticated: true, 
            isInitializing: false,
            isLoading: false 
          })
        } catch (error) {
          console.error('Auth initialization failed:', error)
          localStorage.removeItem('halcode-auth')
          set({ 
            user: null, 
            isAuthenticated: false, 
            isInitializing: false,
            isLoading: false,
            error: 'Session expired'
          })
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.login(email, password)
          const { user, accessToken, refreshToken } = response.data.data || response.data
          
          localStorage.setItem('halcode-auth', JSON.stringify({
            state: { accessToken, refreshToken },
            version: 0
          }))
          
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          const message = error.response?.data?.error || error.response?.data?.message || 'Login failed'
          set({ isLoading: false, error: message })
          throw new Error(message)
        }
      },

      register: async (email: string, password: string, name: string, invitationCode?: string) => {
        set({ isLoading: true, error: null })
        try {
          const payload: { email: string; password: string; name: string; invitationCode?: string } = { email, password, name }
          if (invitationCode) {
            payload.invitationCode = invitationCode
          }
          const response = await authApi.register(email, password, name)
          const { user, accessToken, refreshToken } = response.data.data || response.data
          
          localStorage.setItem('halcode-auth', JSON.stringify({
            state: { accessToken, refreshToken },
            version: 0
          }))
          
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          const message = error.response?.data?.error || error.response?.data?.message || 'Registration failed'
          set({ isLoading: false, error: message })
          throw new Error(message)
        }
      },

      logout: () => {
        localStorage.removeItem('halcode-auth')
        delete api.defaults.headers.common['Authorization']
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        })
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) {
          get().logout()
          return
        }
        try {
          const response = await authApi.refresh(refreshToken)
          const { accessToken, refreshToken: newRefreshToken, user } = response.data.data || response.data
          
          localStorage.setItem('halcode-auth', JSON.stringify({
            state: { accessToken, refreshToken: newRefreshToken },
            version: 0
          }))
          
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          
          set({
            accessToken,
            refreshToken: newRefreshToken,
            user: user || get().user,
            error: null,
          })
        } catch (error: any) {
          console.error('[AuthStore] Token refresh failed:', error.response?.data?.error || error.message)
          get().logout()
        }
      },

      fetchUser: async () => {
        set({ isLoading: true })
        try {
          const response = await authApi.me()
          const { user, accessToken, refreshToken } = response.data.data || response.data
          
          if (accessToken) {
            localStorage.setItem('halcode-auth', JSON.stringify({
              state: { accessToken, refreshToken: refreshToken || get().refreshToken },
              version: 0
            }))
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          }
          
          set({
            user,
            accessToken: accessToken || get().accessToken,
            refreshToken: refreshToken || get().refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          console.error('Failed to fetch user:', error)
          // Try refresh token if access token failed
          const { refreshToken } = get()
          if (refreshToken) {
            try {
              await get().refreshAccessToken()
              const retryResponse = await authApi.me()
              const user = retryResponse.data.data?.user || retryResponse.data.data || retryResponse.data.user
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              })
              return
            } catch (refreshError) {
              console.log('[AuthStore] Refresh failed:', refreshError)
            }
          }
          set({ 
            isLoading: false, 
            isAuthenticated: false, 
            error: 'Session expired'
          })
        }
      },

      updateUser: async (data: Partial<User>) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.patch('/auth/profile', data)
          const updatedUser = response.data.data?.user || response.data.user || response.data
          set((state) => ({
            user: updatedUser ? { ...state.user, ...updatedUser } : state.user,
            isLoading: false
          }))
        } catch (error: any) {
          const message = error.response?.data?.error || error.response?.data?.message || 'Update failed'
          set({ error: message, isLoading: false })
          throw new Error(message)
        }
      },

      checkAuth: async () => {
        const { accessToken, refreshToken } = get()
        
        if (!accessToken && !refreshToken) {
          set({ isLoading: false, isAuthenticated: false, error: null, isInitializing: false })
          return
        }
        
        if (accessToken) {
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
        }
        
        try {
          const response = await authApi.me()
          set({
            user: response.data.data?.user || response.data.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isInitializing: false,
          })
        } catch (error: any) {
          // Try refresh token
          if (refreshToken) {
            await get().refreshAccessToken()
            try {
              const retryResponse = await authApi.me()
              set({
                user: retryResponse.data.data?.user || retryResponse.data.user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
                isInitializing: false,
              })
              return
            } catch (refreshError: any) {
              console.log('[AuthStore] Refresh failed:', refreshError.response?.data?.error || 'Unknown error')
              get().logout()
              return
            }
          }
          set({ isLoading: false, isAuthenticated: false, error: 'Session expired', isInitializing: false })
          get().logout()
        }
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'halcode-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
)

// Initialize auth check on app start
export const initAuth = async () => {
  const { initialize } = useAuthStore.getState()
  await initialize()
}
